(() => {
  const SECTION_SELECTOR = "[data-benefits-timeline]";
  const MOBILE_QUERY = "(max-width: 768px)";
  const DESKTOP_QUERY = "(min-width: 769px)";
  const SWIPE_THRESHOLD = 48;

  function isMobileLayout() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function isScrollDriveEligible(section, stopCount) {
    return (
      window.matchMedia(DESKTOP_QUERY).matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches &&
      section.dataset.enableScrollDrive !== "false" &&
      stopCount > 1
    );
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function getPageHeaderOffset() {
    const header = document.querySelector(
      "header.page-header, header.shopify-section.page-header",
    );
    if (!header) return null;
    const rect = header.getBoundingClientRect();
    const offset = Math.round(rect.bottom);
    return offset > 0 ? offset : null;
  }

  function syncStickyTopOffset(section) {
    const offset = getPageHeaderOffset();
    if (offset != null) {
      section.style.setProperty("--tl-sticky-top", `${offset}px`);
      return;
    }
    section.style.removeProperty("--tl-sticky-top");
  }

  /** How many stops fit in the track at the current viewport width. */
  function getMaxVisibleStops(width = window.innerWidth) {
    if (width <= 768) return 1;
    if (width < 820) return 3;
    if (width <= 1024) return 5;
    if (width <= 1280) return 6;
    return Number.POSITIVE_INFINITY;
  }

  function getActiveFr(width = window.innerWidth) {
    if (width <= 768) return 1;
    if (width <= 1024) return 1.78;
    return 2.05;
  }

  function getInactiveFr(width = window.innerWidth) {
    if (width <= 1024) return 1.35;
    if (width <= 1280) return 1.4;
    return 1.25;
  }

  /** Contiguous window of stops in timeline order; active expands in place. */
  function getVisibleRange(activeIdx, total, maxVisible) {
    if (maxVisible >= total) {
      const indices = Array.from({ length: total }, (_, i) => i);
      return { start: 0, end: total - 1, indices };
    }

    const windowSize = Math.max(1, maxVisible);
    const half = Math.floor(windowSize / 2);
    let start = Math.max(0, activeIdx - half);
    let end = start + windowSize - 1;

    if (end >= total) {
      end = total - 1;
      start = Math.max(0, end - windowSize + 1);
    }

    const indices = [];
    for (let i = start; i <= end; i += 1) {
      indices.push(i);
    }

    return { start, end, indices };
  }

  function buildTrackColumns(visibleIndices, activeIdx, activeFr = 1.55, inactiveFr = 0.72) {
    if (!visibleIndices.length) return "";
    return visibleIndices
      .map((stopIdx) => `${stopIdx === activeIdx ? activeFr : inactiveFr}fr`)
      .join(" ");
  }

  /** Soft peak between adjacent stops while scrolling. */
  function scrollDriveFocus(stopIdx, rawIndex) {
    const distance = Math.abs(stopIdx - rawIndex);
    if (distance >= 1) return 0;
    const t = 1 - distance;
    return t * t * (3 - 2 * t);
  }

  function parseStops(section) {
    const el = section.querySelector("[data-timeline-stops]");
    if (!el) return [];
    try {
      const data = JSON.parse(el.textContent);
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  }

  function tintClass(tint) {
    if (!tint || tint === "default") return "";
    return tint.startsWith("tint-") ? tint : `tint-${tint}`;
  }

  function buildImage(stop) {
    const wrap = document.createElement("div");
    const classes = ["some-benefits-timeline__img", tintClass(stop.tint)].filter(Boolean);
    wrap.className = classes.join(" ");

    if (stop.image) {
      wrap.classList.add("has-media");
      const img = document.createElement("img");
      img.className = "some-benefits-timeline__img-media";
      img.src = stop.image;
      img.alt = stop.when || "";
      img.loading = "lazy";
      img.width = 600;
      img.height = 600;
      wrap.appendChild(img);
    } else {
      const stripes = document.createElement("div");
      stripes.className = "some-benefits-timeline__stripes";
      stripes.setAttribute("aria-hidden", "true");
      wrap.appendChild(stripes);

      if (stop.placeholder_mark) {
        const mark = document.createElement("span");
        mark.className = "some-benefits-timeline__ph-mark";
        mark.textContent = stop.placeholder_mark;
        wrap.appendChild(mark);
      }

      if (stop.placeholder_label) {
        const label = document.createElement("span");
        label.className = "some-benefits-timeline__ph-label";
        label.textContent = stop.placeholder_label;
        wrap.appendChild(label);
      }
    }

    return wrap;
  }

  function toSentenceCase(text) {
    if (!text || text !== text.toUpperCase()) return text;
    return text
      .toLowerCase()
      .replace(/(^\s*\w|[.!?]\s+\w)/g, (match) => match.toUpperCase());
  }

  function buildBody(stop) {
    const body = document.createElement("div");
    body.className = "some-benefits-timeline__body";

    if (stop.copy) {
      const copy = document.createElement("p");
      copy.className = "some-benefits-timeline__copy";
      copy.textContent = toSentenceCase(stop.copy);
      body.appendChild(copy);
    }

    return body;
  }

  function buildStopButton(stop, stopIdx, onSelect) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "some-benefits-timeline__stop is-inactive";
    btn.setAttribute("aria-pressed", "false");
    btn.dataset.idx = String(stopIdx);

    btn.appendChild(buildImage(stop));

    const meta = document.createElement("div");
    meta.className = "some-benefits-timeline__meta";

    const when = document.createElement("span");
    when.className = "some-benefits-timeline__when";
    when.textContent = toSentenceCase(stop.when || "");
    meta.appendChild(when);

    meta.appendChild(buildBody(stop));
    btn.appendChild(meta);
    btn.addEventListener("click", () => onSelect(stopIdx));
    return btn;
  }

  function initTimeline(section) {
    if (section.dataset.timelineInit === "1") return;

    const stops = parseStops(section);
    const row = section.querySelector("[data-timeline-row]");
    const track = section.querySelector("[data-timeline-track]");
    const ticks = section.querySelector("[data-timeline-ticks]");
    const dots = section.querySelector("[data-timeline-dots]");
    const counter = section.querySelector("[data-timeline-counter]");
    const prevBtn = section.querySelector("[data-timeline-prev]");
    const nextBtn = section.querySelector("[data-timeline-next]");
    const enableKeyboard = section.dataset.keyboardNav === "true";
    const autoplayEnabledSetting = section.dataset.enableAutoplay === "true";
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const autoplayIntervalMs = Math.max(
      3000,
      (parseInt(section.dataset.autoplayInterval, 10) || 10) * 1000
    );
    let autoplayTimer = null;
    let autoplayPaused = false;
    let isInView = true;
    let lastActiveIdx = Math.min(
      Math.max(0, (parseInt(section.dataset.initialStop, 10) || 1) - 1),
      stops.length - 1
    );

    if (!row || stops.length === 0) {
      section.classList.add("is-empty");
      return;
    }

    section.classList.remove("is-empty");
    section.dataset.timelineInit = "1";

    let activeIdx = Math.min(
      Math.max(0, (parseInt(section.dataset.initialStop, 10) || 1) - 1),
      stops.length - 1
    );

    row.innerHTML = "";
    if (ticks) ticks.innerHTML = "";
    if (dots) dots.innerHTML = "";

    row.style.setProperty("--stop-count", stops.length);

    const stopEls = stops.map((stop, i) => {
      const el = buildStopButton(stop, i, setActive);
      row.appendChild(el);
      return el;
    });

    let scrollSyncFrame = null;
    let isScrollSyncing = false;
    let scrollDriveEnabled = false;
    let scrollDriveRaf = null;
    let scrollDriveHandler = null;
    let progressEl = null;
    let progressFillEl = null;
    let scrollDriveRawIndex = 0;
    let lastDriveGridKey = "";
    let scrollDriveMetrics = null;

    if (track) {
      progressEl = document.createElement("div");
      progressEl.className = "some-benefits-timeline__scroll-progress";
      progressEl.hidden = true;
      progressEl.setAttribute("aria-hidden", "true");

      const progressTrack = document.createElement("div");
      progressTrack.className = "some-benefits-timeline__scroll-progress-track";

      progressFillEl = document.createElement("div");
      progressFillEl.className = "some-benefits-timeline__scroll-progress-fill";
      progressTrack.appendChild(progressFillEl);
      progressEl.appendChild(progressTrack);

      track.insertBefore(progressEl, track.firstChild);
    }

    const tickEls = [];
    if (ticks) {
      const initialVisible = getVisibleRange(activeIdx, stops.length, getMaxVisibleStops());
      ticks.style.gridTemplateColumns = buildTrackColumns(
        initialVisible.indices,
        activeIdx,
        getActiveFr(),
        getInactiveFr()
      );
      for (let i = 0; i < stops.length; i++) {
        const t = document.createElement("div");
        t.className = "some-benefits-timeline__tick";
        t.dataset.tick = String(i);
        ticks.appendChild(t);
        tickEls.push(t);
      }
    }

    const dotEls = [];
    if (dots) {
      stops.forEach((s, i) => {
        const labelText = toSentenceCase(s.when || s.scrub || `Stop ${i + 1}`);
        const d = document.createElement("button");
        d.type = "button";
        d.className = "some-benefits-timeline__scrub-dot";
        d.dataset.dot = String(i);
        d.setAttribute("role", "tab");
        d.setAttribute("aria-selected", "false");
        d.setAttribute("aria-label", `Show ${labelText}`);

        const label = document.createElement("span");
        label.className = "some-benefits-timeline__carousel-label";
        label.textContent = labelText;
        d.appendChild(label);

        const ttip = document.createElement("span");
        ttip.className = "some-benefits-timeline__ttip";
        ttip.textContent = s.scrub || s.when || "";
        d.appendChild(ttip);

        d.addEventListener("click", () => setActive(i));
        dots.appendChild(d);
        dotEls.push(d);
      });
    }

    function scrollCarouselToElement(container, element, behavior = "smooth", align = "center") {
      if (!container || !element) return;
      const targetLeft =
        align === "start"
          ? element.offsetLeft
          : element.offsetLeft - (container.clientWidth - element.clientWidth) / 2;
      container.scrollTo({
        left: Math.max(0, targetLeft),
        behavior,
      });
    }

    function scrollRowToActive(behavior = "smooth") {
      if (!isMobileLayout() || !row) return;
      scrollCarouselToElement(row, stopEls[activeIdx], behavior, "start");
    }

    function scrollDotsToActive(behavior = "smooth") {
      if (!isMobileLayout() || !dots) return;
      scrollCarouselToElement(dots, dotEls[activeIdx], behavior);
    }

    function updateChrome() {
      const mobile = isMobileLayout();
      const maxVisible = getMaxVisibleStops();
      const visible = getVisibleRange(activeIdx, stops.length, maxVisible);
      const visibleSet = new Set(visible.indices);
      const inactiveFr = getInactiveFr();

      section.classList.toggle("is-mobile-layout", mobile);
      section.classList.toggle("is-tablet-layout", !mobile && maxVisible < stops.length);
      row.setAttribute("aria-roledescription", mobile ? "carousel" : "group");

      if (!mobile && scrollDriveEnabled) {
        applyScrollDriveFrame();
        dotEls.forEach((d, i) => {
          const isActive = i === activeIdx;
          d.classList.toggle("is-active", isActive);
          d.setAttribute("aria-selected", isActive ? "true" : "false");
        });
        return;
      }

      stopEls.forEach((el, i) => {
        const isActive = i === activeIdx;
        const isHidden = mobile ? false : !visibleSet.has(i);

        el.classList.toggle("is-active", isActive);
        el.classList.toggle("is-inactive", !isActive);
        el.classList.toggle("is-hidden", isHidden);
        el.setAttribute("aria-pressed", isActive ? "true" : "false");
        el.setAttribute("aria-hidden", isHidden ? "true" : "false");
        el.tabIndex = isHidden ? -1 : 0;
      });

      if (activeIdx > lastActiveIdx) {
        section.dataset.slideDirection = "next";
      } else if (activeIdx < lastActiveIdx) {
        section.dataset.slideDirection = "prev";
      } else {
        delete section.dataset.slideDirection;
      }

      if (!mobile) {
        const columns = buildTrackColumns(visible.indices, activeIdx, getActiveFr(), inactiveFr);
        row.style.gridTemplateColumns = columns;
        if (ticks) ticks.style.gridTemplateColumns = columns;
      } else {
        row.style.gridTemplateColumns = "";
        if (ticks) ticks.style.gridTemplateColumns = "";
      }

      if (ticks) {
        ticks.querySelectorAll(".some-benefits-timeline__tick").forEach((t, i) => {
          t.classList.toggle("is-hidden", !visibleSet.has(i));
          t.classList.toggle("is-active", i === activeIdx);
        });
      }

      dotEls.forEach((d, i) => {
        const isActive = i === activeIdx;
        d.classList.toggle("is-active", isActive);
        d.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      if (counter) {
        counter.classList.add("is-updating");
        const n = String(activeIdx + 1).padStart(2, "0");
        const total = String(stops.length).padStart(2, "0");
        counter.innerHTML = `<b>${n}</b>&nbsp;/&nbsp;${total}`;
        window.setTimeout(() => counter.classList.remove("is-updating"), 360);
      }

      if (prevBtn) prevBtn.disabled = activeIdx === 0;
      if (nextBtn) nextBtn.disabled = activeIdx === stops.length - 1;

      lastActiveIdx = activeIdx;
    }

    function setActive(i, { fromAutoplay = false, fromScroll = false, fromScrollDrive = false } = {}) {
      if (i < 0 || i >= stops.length || i === activeIdx) return;

      if (!prefersReducedMotion && !fromScroll && !fromScrollDrive) {
        section.classList.add("is-switching");
        window.clearTimeout(section._switchTimer);
        section._switchTimer = window.setTimeout(() => {
          section.classList.remove("is-switching");
        }, 700);
      }

      activeIdx = i;
      updateChrome();

      if (isMobileLayout()) {
        if (!fromScroll) {
          isScrollSyncing = true;
          scrollRowToActive(fromScroll ? "auto" : "smooth");
          window.setTimeout(() => {
            isScrollSyncing = false;
          }, 420);
        }
        scrollDotsToActive(fromScroll ? "auto" : "smooth");
      }

      if (fromAutoplay) {
        scheduleAutoplay();
      } else if (!fromScroll && !fromScrollDrive) {
        resetAutoplay();
      }
    }

    function refreshScrollDriveMetrics() {
      const stage = section.querySelector("[data-timeline-scroll-stage]");
      if (!stage) {
        scrollDriveMetrics = null;
        return null;
      }

      const rect = stage.getBoundingClientRect();
      scrollDriveMetrics = {
        stageTop: window.scrollY + rect.top,
        scrollable: Math.max(1, stage.offsetHeight - window.innerHeight),
      };
      return scrollDriveMetrics;
    }

    function getScrollDriveProgress() {
      const metrics = scrollDriveMetrics || refreshScrollDriveMetrics();
      if (!metrics) return 0;
      return clamp((window.scrollY - metrics.stageTop) / metrics.scrollable, 0, 1);
    }

    function clearScrollDriveStopStyles() {
      stopEls.forEach((el) => {
        el.style.removeProperty("--tl-focus");
      });
      section.style.removeProperty("--tl-progress");
    }

    function applyScrollDriveFrame() {
      if (!scrollDriveEnabled || !row) return;

      const progress = getScrollDriveProgress();
      const rawIndex = progress * Math.max(1, stops.length - 1);
      const focusIdx = Math.min(stops.length - 1, Math.round(rawIndex));
      scrollDriveRawIndex = rawIndex;

      section.style.setProperty("--tl-progress", progress.toFixed(4));
      if (progressFillEl) {
        progressFillEl.style.transform = `scaleX(${progress})`;
      }

      const maxVisible = getMaxVisibleStops();
      const visible = getVisibleRange(focusIdx, stops.length, maxVisible);
      const visibleSet = new Set(visible.indices);
      const activeFr = getActiveFr();
      const inactiveFr = getInactiveFr();

      // Original wider active column — updated on stop change only, eased in CSS (not per-frame).
      const gridKey = `${visible.start}-${visible.end}-${focusIdx}`;
      if (gridKey !== lastDriveGridKey) {
        lastDriveGridKey = gridKey;
        const columns = buildTrackColumns(visible.indices, focusIdx, activeFr, inactiveFr);
        row.style.gridTemplateColumns = columns;
        if (ticks) ticks.style.gridTemplateColumns = columns;
      }

      stopEls.forEach((el, i) => {
        const focus = scrollDriveFocus(i, rawIndex);
        el.style.setProperty("--tl-focus", focus.toFixed(3));
        const isHidden = !visibleSet.has(i);
        el.classList.toggle("is-hidden", isHidden);
        el.classList.toggle("is-active", i === focusIdx);
        el.classList.toggle("is-inactive", i !== focusIdx);
        el.setAttribute("aria-pressed", i === focusIdx ? "true" : "false");
        el.setAttribute("aria-hidden", isHidden ? "true" : "false");
        el.tabIndex = isHidden ? -1 : 0;
      });

      if (ticks) {
        ticks.querySelectorAll(".some-benefits-timeline__tick").forEach((t, i) => {
          t.classList.toggle("is-hidden", !visibleSet.has(i));
          t.classList.toggle("is-active", Math.abs(i - rawIndex) < 0.35);
        });
      }

      if (counter && focusIdx !== activeIdx) {
        const n = String(focusIdx + 1).padStart(2, "0");
        const total = String(stops.length).padStart(2, "0");
        counter.innerHTML = `<b>${n}</b>&nbsp;/&nbsp;${total}`;
      }

      if (prevBtn) prevBtn.disabled = focusIdx === 0;
      if (nextBtn) nextBtn.disabled = focusIdx === stops.length - 1;

      activeIdx = focusIdx;
      lastActiveIdx = focusIdx;
    }

    function updateScrollDriveIndex() {
      if (!scrollDriveEnabled) return;
      applyScrollDriveFrame();
    }

    function teardownScrollDrive() {
      if (scrollDriveHandler) {
        window.removeEventListener("scroll", scrollDriveHandler);
        scrollDriveHandler = null;
      }
      if (scrollDriveRaf) {
        window.cancelAnimationFrame(scrollDriveRaf);
        scrollDriveRaf = null;
      }

      const stage = section.querySelector("[data-timeline-scroll-stage]");
      if (stage) {
        const wrap = stage.querySelector(".some-benefits-timeline__sectionWrap");
        if (wrap && stage.parentNode) {
          stage.parentNode.insertBefore(wrap, stage);
          stage.remove();
        }
      }

      clearScrollDriveStopStyles();
      lastDriveGridKey = "";
      scrollDriveMetrics = null;
      if (progressEl) progressEl.hidden = true;
      section.classList.remove("is-scroll-driven");
      scrollDriveEnabled = false;
      updateChrome();
    }

    function setupScrollDrive() {
      if (scrollDriveEnabled) {
        teardownScrollDrive();
      }
      if (!isScrollDriveEligible(section, stops.length)) return;

      const wrap = section.querySelector(".some-benefits-timeline__sectionWrap");
      if (!wrap || wrap.closest("[data-timeline-scroll-stage]")) return;

      const stage = document.createElement("div");
      stage.className = "some-benefits-timeline__scroll-stage";
      stage.dataset.timelineScrollStage = "";
      stage.style.setProperty("--tl-scroll-steps", String(stops.length));

      const pin = document.createElement("div");
      pin.className = "some-benefits-timeline__scroll-pin";
      pin.dataset.timelineScrollPin = "";

      const parent = wrap.parentNode;
      if (!parent) return;

      parent.insertBefore(stage, wrap);
      stage.appendChild(pin);
      pin.appendChild(wrap);

      syncStickyTopOffset(section);
      section.classList.add("is-scroll-driven");
      scrollDriveEnabled = true;
      if (progressEl) progressEl.hidden = false;
      stopAutoplay();

      scrollDriveHandler = () => {
        if (scrollDriveRaf) return;
        scrollDriveRaf = window.requestAnimationFrame(() => {
          scrollDriveRaf = null;
          updateScrollDriveIndex();
        });
      };

      window.addEventListener("scroll", scrollDriveHandler, { passive: true });
      refreshScrollDriveMetrics();
      updateScrollDriveIndex();
    }

    function canAutoplay() {
      return (
        autoplayEnabledSetting &&
        !prefersReducedMotion &&
        !scrollDriveEnabled &&
        stops.length > 1 &&
        isInView &&
        !autoplayPaused &&
        !document.hidden
      );
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        window.clearTimeout(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function scheduleAutoplay() {
      stopAutoplay();
      if (!canAutoplay()) return;

      autoplayTimer = window.setTimeout(() => {
        setActive((activeIdx + 1) % stops.length, { fromAutoplay: true });
      }, autoplayIntervalMs);
    }

    function resetAutoplay() {
      stopAutoplay();
      scheduleAutoplay();
    }

    function pauseAutoplay() {
      autoplayPaused = true;
      stopAutoplay();
    }

    function resumeAutoplay() {
      autoplayPaused = false;
      scheduleAutoplay();
    }

    if (prevBtn) prevBtn.addEventListener("click", () => setActive(activeIdx - 1));
    if (nextBtn) nextBtn.addEventListener("click", () => setActive(activeIdx + 1));

    if (enableKeyboard) {
      section.addEventListener("keydown", (e) => {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          setActive(activeIdx - 1);
        }
        if (e.key === "ArrowRight") {
          e.preventDefault();
          setActive(activeIdx + 1);
        }
      });
      section.tabIndex = 0;
    }

    if (row) {
      row.addEventListener(
        "scroll",
        () => {
          if (!isMobileLayout() || isScrollSyncing) return;

          window.cancelAnimationFrame(scrollSyncFrame);
          scrollSyncFrame = window.requestAnimationFrame(() => {
            const scrollLeft = row.scrollLeft;
            let closestIdx = activeIdx;
            let closestDistance = Number.POSITIVE_INFINITY;

            stopEls.forEach((el, i) => {
              const distance = Math.abs(el.offsetLeft - scrollLeft);
              if (distance < closestDistance) {
                closestDistance = distance;
                closestIdx = i;
              }
            });

            if (closestIdx !== activeIdx) {
              pauseAutoplay();
              window.setTimeout(resumeAutoplay, autoplayIntervalMs);
              setActive(closestIdx, { fromScroll: true });
            }
          });
        },
        { passive: true }
      );
    }

    if (track) {
      let touchStartX = 0;
      let touchStartY = 0;

      track.addEventListener(
        "touchstart",
        (e) => {
          pauseAutoplay();
          window.setTimeout(resumeAutoplay, autoplayIntervalMs);

          if (isMobileLayout()) return;
          const touch = e.changedTouches[0];
          touchStartX = touch.screenX;
          touchStartY = touch.screenY;
        },
        { passive: true }
      );

      track.addEventListener(
        "touchend",
        (e) => {
          if (isMobileLayout()) return;
          const touch = e.changedTouches[0];
          const deltaX = touch.screenX - touchStartX;
          const deltaY = touch.screenY - touchStartY;

          if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
          if (Math.abs(deltaX) < Math.abs(deltaY)) return;

          if (deltaX < 0) setActive(activeIdx + 1);
          else setActive(activeIdx - 1);
        },
        { passive: true }
      );
    }

    const mobileMq = window.matchMedia(MOBILE_QUERY);
    const onBreakpointChange = () => {
      setupScrollDrive();
      updateChrome();
      if (isMobileLayout()) {
        isScrollSyncing = true;
        scrollRowToActive("auto");
        scrollDotsToActive("auto");
        window.requestAnimationFrame(() => {
          isScrollSyncing = false;
        });
      } else {
        updateScrollDriveIndex();
      }
      resetAutoplay();
    };
    if (mobileMq.addEventListener) {
      mobileMq.addEventListener("change", onBreakpointChange);
    } else {
      mobileMq.addListener(onBreakpointChange);
    }

    let resizeTimer;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        setupScrollDrive();
        updateChrome();
        refreshScrollDriveMetrics();
        updateScrollDriveIndex();
      }, 120);
    };
    window.addEventListener("resize", onResize, { passive: true });

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        scheduleAutoplay();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const intersectionObserver =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              isInView = entries.some((entry) => entry.isIntersecting);
              if (isInView) {
                scheduleAutoplay();
              } else {
                stopAutoplay();
              }
            },
            { threshold: 0.2 }
          )
        : null;

    if (intersectionObserver) {
      intersectionObserver.observe(section);
    }

    section.addEventListener("mouseenter", pauseAutoplay);
    section.addEventListener("mouseleave", resumeAutoplay);
    section.addEventListener("focusin", pauseAutoplay);
    section.addEventListener("focusout", (e) => {
      if (section.contains(e.relatedTarget)) return;
      resumeAutoplay();
    });

    updateChrome();
    setupScrollDrive();

    if (isMobileLayout()) {
      isScrollSyncing = true;
      scrollRowToActive("auto");
      scrollDotsToActive("auto");
      window.requestAnimationFrame(() => {
        isScrollSyncing = false;
      });
    } else {
      updateScrollDriveIndex();
    }

    scheduleAutoplay();

    section._timelineReset = () => {
      teardownScrollDrive();
      stopAutoplay();
      window.removeEventListener("resize", onResize);
      window.clearTimeout(resizeTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
      if (mobileMq.removeEventListener) {
        mobileMq.removeEventListener("change", onBreakpointChange);
      } else {
        mobileMq.removeListener(onBreakpointChange);
      }
      delete section.dataset.timelineInit;
      initTimeline(section);
    };
  }

  function initAll(root = document) {
    root.querySelectorAll(SECTION_SELECTOR).forEach(initTimeline);
  }

  function resetSection(section) {
    if (section._timelineReset) {
      section._timelineReset();
    } else {
      delete section.dataset.timelineInit;
      initTimeline(section);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (e) => {
    const container = e.target;
    const section =
      container?.matches?.(SECTION_SELECTOR)
        ? container
        : container?.querySelector?.(SECTION_SELECTOR);
    if (section) resetSection(section);
  });

  document.addEventListener("shopify:block:select", (e) => {
    const section = e.target.closest?.(SECTION_SELECTOR);
    if (section) resetSection(section);
  });
})();
